import React, { useEffect, useMemo, useState } from "react";
import { array, bool, func, object, string } from "prop-types";
import { LazyLoadImages } from "@quintype/components";
import { get } from "lodash";
import { BrowserView, MobileView } from "react-device-detect";
import { useSelector } from "react-redux";
import { getCreatorSocialTitle } from "../../utils";
import { getStoryUrl } from "../../../arrow/utils/utils";
import { DfpComponent } from "../../ads/dfp-component";
import { StoryHeader } from "../../molecules";
import StoryElements from "../story-elements";
import { LatestMagazineIssues } from "../../collection-templates/latest-magazine-issues";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";
import { getFilteredMagazineCollection, getMagazineCollectionDetails } from "../../../../api/utils";
import "../../pages/story.m.css";

const HTML_BLOCK_REGEX = /(<([^>\s]+)(?:\s[^>]*)?>[\s\S]*?<\/\2>|<[^>\s]+(?:\s[^>]*)?\/>)/g;

const isAdPlaceholderBlock = (block = "") => {
  const blockText = block.replace(/<[^>]*>/g, "").trim();
  return blockText.includes('"flag":"ad_goes_here"') || blockText.includes('"flag": "ad_goes_here"');
};

const isBodyTextElement = (element = {}) =>
  element.type === "text" && (element.subtype === null || element.subtype === undefined);

const getReadableBlocks = (text = "") => {
  const htmlBlocks = text?.match(HTML_BLOCK_REGEX) || [];
  const paragraphBlocks = htmlBlocks.filter((block) => /^<p(?:\s|>)/i.test(block));
  const readableBlocks = (paragraphBlocks.length ? paragraphBlocks : htmlBlocks).filter(
    (block) => !isAdPlaceholderBlock(block)
  );

  if (readableBlocks.length) {
    return readableBlocks;
  }

  return text?.replace(/<[^>]*>/g, "").trim() && !isAdPlaceholderBlock(text) ? [text] : [];
};

const getReadableParagraphCount = (cards = []) =>
  cards.reduce((paragraphCount, card) => {
    const cardTextElements = get(card, ["story-elements"], []).filter(isBodyTextElement);

    return (
      paragraphCount +
      cardTextElements.reduce((textElementParagraphCount, element) => {
        return textElementParagraphCount + getReadableBlocks(element.text).length;
      }, 0)
    );
  }, 0);

const getPreviewText = (text = "", remainingBlocks = 0) => {
  if (remainingBlocks <= 0) {
    return { text: "", usedBlocks: 0 };
  }

  const htmlBlocks = text.match(HTML_BLOCK_REGEX) || [];
  const previewBlocks = [];
  let usedBlocks = 0;
  const hasParagraphBlocks = htmlBlocks.some((htmlBlock) => /^<p(?:\s|>)/i.test(htmlBlock));

  for (const block of htmlBlocks) {
    if (isAdPlaceholderBlock(block)) {
      previewBlocks.push(block);
      continue;
    }

    if (!/^<p(?:\s|>)/i.test(block) && hasParagraphBlocks) {
      continue;
    }

    previewBlocks.push(block);
    usedBlocks += 1;

    if (usedBlocks >= remainingBlocks) {
      break;
    }
  }

  if (!previewBlocks.length && text.replace(/<[^>]*>/g, "").trim() && !isAdPlaceholderBlock(text)) {
    return { text, usedBlocks: 1 };
  }

  return { text: previewBlocks.join(""), usedBlocks };
};

const getContinueReadingPreviewCards = (cards = [], previewParagraphCount = 3) => {
  const previewCards = [];
  let remainingBlocks = previewParagraphCount;

  for (const card of cards) {
    if (remainingBlocks <= 0) {
      break;
    }

    const previewElements = [];

    for (const element of get(card, ["story-elements"], [])) {
      if (remainingBlocks <= 0) {
        break;
      }

      if (!isBodyTextElement(element)) {
        previewElements.push(element);
        continue;
      }

      const previewText = getPreviewText(element.text, remainingBlocks);

      if (!previewText.text) {
        continue;
      }

      previewElements.push({ ...element, text: previewText.text });
      remainingBlocks -= previewText.usedBlocks;
    }

    if (previewElements.length) {
      previewCards.push({ ...card, "story-elements": previewElements });
    }
  }

  return previewCards;
};

const preserveScrollPosition = (scrollY) => {
  if (typeof window === "undefined" || typeof scrollY !== "number") return;

  window.requestAnimationFrame(() => {
    window.scrollTo({ top: scrollY, behavior: "auto" });
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY, behavior: "auto" });
    });
  });
};

const trackContinueReadingClick = ({ story, config, previewParagraphCount }) => {
  if (typeof window === "undefined") return;

  const pageType = "story-page";
  const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, config, story);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "dl_story_interactions",
    event_action: "readmore_click",
    category,
    sub_category: subCategory,
    content_id: contentId,
    template_name: getTemplateName(story?.["story-template"] || pageType),
    platform: window.innerWidth >= 768 ? "desktop" : "mobile",
    preview_paragraph_count: previewParagraphCount,
  });
};

const getStoryContentId = (story = {}) => story["story-content-id"] || story.id;

const getAdStoryKey = (story = {}) =>
  (story["story-content-id"] || story["public-identifier"] || story["sequence-no"] || story.id || "story")
    .toString()
    .replace(/[^a-zA-Z0-9_-]/g, "-");

const syncContinueReadingStoryState = ({ story, isPerpetualStory }) => {
  if (typeof window === "undefined") return;

  const storyId = getStoryContentId(story);
  const storyUrl = getStoryUrl(story, story?.slug ? `/${story.slug}` : "");

  if (storyUrl && storyUrl !== window.location.pathname) {
    window.history.replaceState({ storyId }, "", storyUrl);
  }

  const storyTitle = get(story, ["seo", "meta-title"], "") || story?.headline;
  if (typeof document !== "undefined" && storyTitle && storyTitle !== document.title) {
    document.title = storyTitle;
  }

  window.dispatchEvent(new CustomEvent("perpetual-story-active", { detail: { story, isPerpetualStory } }));
};

const fetchMagazineDetails = async (story) => {
  const storyId = getStoryContentId(story);
  if (!storyId) return null;

  try {
    const storyIssueSlug = get(story, ["metadata", "story-attributes", "magazineissue", 0], "");
    const collectionDetails = storyIssueSlug && (await getMagazineCollectionDetails(storyIssueSlug));

    if (collectionDetails?.data) {
      return collectionDetails;
    }

    const { issueSlug, magazineSlug } = await getFilteredMagazineCollection(storyId);
    if (!issueSlug) return null;

    return getMagazineCollectionDetails(issueSlug, magazineSlug);
  } catch (error) {
    console.error("Error fetching magazine details:", error);
    return null;
  }
};

const ContinueReadingAd = ({ story }) => {
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const qtConfig = get(qtState, ["config"], {});
  const isAdfreePage = get(qtConfig, ["subscriber"], "") === "true";
  const networkId = get(qtConfig, ["ads-config", "network_id"], "");
  const storyKey = getAdStoryKey(story);

  if (isAdfreePage || !networkId) {
    return null;
  }

  return (
    <div styleName="continue-reading-ad">
      <BrowserView>
        <DfpComponent
          adStyleName="ad-slot-size-728x90-300x250"
          id={`story-page-read-more-leaderboard-${storyKey}`}
          path={`/${networkId}/RPSG_Fortune_Desktop/RPSG_FRTN_Desk_AS_Leaderboard_1`}
          adSlotConfigName="leaderboard_ad"
          showText={true}
          targetStory={story}
        />
      </BrowserView>
      <MobileView>
        <DfpComponent
          adStyleName="ad-slot-size-300x250"
          id={`story-page-read-more-mrec-${storyKey}`}
          path={`/${networkId}/RPSG_Fortune_Mob/RPSG_FRTN_Mob_AS_mREC`}
          adSlotConfigName="mRec_ad"
          showText={true}
          targetStory={story}
        />
      </MobileView>
    </div>
  );
};

ContinueReadingAd.propTypes = {
  story: object,
};

const TextStory = ({
  story,
  config,
  adWidget,
  adPlaceholder,
  hasAccess,
  recommendedStories,
  magazineDetails,
  continueReading,
  hideBreadcrumb,
  isPerpetualStory,
  storyInstanceId,
}) => {
  const { headline, authors, "published-at": publishTime = "" } = story;
  const [isContinueReadingExpanded, setIsContinueReadingExpanded] = useState(false);
  const [perpetualMagazineDetails, setPerpetualMagazineDetails] = useState(null);
  const member = useSelector((state) => get(state, ["member"], null));
  const isWebView = useSelector((state) => get(state, ["qt", "config", "isWebView"], false) === true);
  const isMagazineStory = story["story-template"] === "magazine";
  const storyContentId = getStoryContentId(story);
  const canUseContinueReading = continueReading?.enabled && (!isMagazineStory || member);
  const previewParagraphCount = continueReading?.previewParagraphCount || 3;
  const readableParagraphCount = useMemo(() => getReadableParagraphCount(story.cards), [story.cards]);
  const hasMoreThanPreviewParagraphs = readableParagraphCount > previewParagraphCount;
  const shouldShowContinueReadingPreview =
    canUseContinueReading && hasMoreThanPreviewParagraphs && !isContinueReadingExpanded;
  const previewCards = useMemo(
    () => getContinueReadingPreviewCards(story.cards, previewParagraphCount),
    [story.cards, previewParagraphCount]
  );
  const storyCards = shouldShowContinueReadingPreview ? previewCards : story.cards;
  const effectiveMagazineDetails = magazineDetails || perpetualMagazineDetails?.data;

  const handleContinueReadingClick = () => {
    const scrollY = typeof window !== "undefined" ? window.scrollY : null;

    trackContinueReadingClick({ story, config, previewParagraphCount });
    syncContinueReadingStoryState({ story, isPerpetualStory });
    setIsContinueReadingExpanded(true);
    preserveScrollPosition(scrollY);
  };

  useEffect(() => {
    let isMounted = true;

    setPerpetualMagazineDetails(null);

    if (!isPerpetualStory || !isMagazineStory || magazineDetails || !storyContentId) {
      return () => {
        isMounted = false;
      };
    }

    fetchMagazineDetails(story).then((details) => {
      if (isMounted) {
        setPerpetualMagazineDetails(details);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [isMagazineStory, isPerpetualStory, magazineDetails, storyContentId]);

  return (
    <div styleName="story-template-container" className="story-holder">
      <StoryHeader
        story={story}
        headline={headline}
        storyAuthorData={authors}
        publishTime={publishTime}
        readTime={story?.["read-time"]}
        magazineDetails={effectiveMagazineDetails}
        hideBreadcrumb={hideBreadcrumb}
        isContinueReadingPreviewActive={shouldShowContinueReadingPreview}
        isPerpetualStory={isPerpetualStory}
        storyInstanceId={storyInstanceId}
      />
      <div styleName="continue-reading-height-preview">
        <div className="story-elements">
          <div className="text-container">
            <LazyLoadImages>
              <StoryElements
                story={story}
                cards={storyCards}
                recommendedStories={recommendedStories}
                config={config}
                isContinueReadingPreview={shouldShowContinueReadingPreview}
                disableRecommendedStories={isPerpetualStory}
                isPerpetualStory={isPerpetualStory}
                storyInstanceId={storyInstanceId}
              />
            </LazyLoadImages>
          </div>
        </div>
        {shouldShowContinueReadingPreview && (
          <div styleName="continue-reading-wrapper">
            <button type="button" styleName="continue-reading-button" onClick={handleContinueReadingClick}>
              Read Full Story
            </button>
          </div>
        )}
        {/* {shouldShowContinueReadingPreview && <ContinueReadingAd story={story} />} */}
        {!shouldShowContinueReadingPreview && isPerpetualStory && isMagazineStory && member && !isWebView && (
          <div styleName="story-elements-wrapper">
            <LatestMagazineIssues isStoryPage={true} story={story} config={config} />
          </div>
        )}
        {/* {!shouldShowContinueReadingPreview && isPerpetualStory && isContinueReadingExpanded && (
          <ContinueReadingAd story={story} />
        )} */}
      </div>
    </div>
  );
};

TextStory.propTypes = {
  story: object,
  config: object,
  adWidget: func,
  adPlaceholder: object,
  hasAccess: bool,
  recommendedStories: array,
  magazineDetails: object,
  continueReading: object,
  hideBreadcrumb: bool,
  isPerpetualStory: bool,
  storyInstanceId: string,
};

export default TextStory;
